/**
 * MonsterSchema.js - Comprehensive Monster Stat Block Schema
 * 
 * This file defines the master JSON schema for all monster stat blocks in the Virtual Combat Simulator.
 * It supports all D&D 5e/2024 features from basic creatures to complex legendary/mythic beings.
 * 
 * SCHEMA FEATURES:
 * - Complete D&D 5e/2024 compatibility
 * - Legendary, Lair, and Mythic actions
 * - Complex spellcasting (innate and prepared)
 * - Damage modifiers with qualifiers
 * - Flexible action economy
 * - Source attribution and edition tracking
 * - System extensions for custom rules
 * 
 * USAGE:
 * - Use validateMonster(monster) to validate against schema
 * - Use migrateOldMonster(oldMonster) to convert legacy data
 * - Use createEmptyMonster() for new monster templates
 */

// Master Monster Schema (JSON Schema Draft 7)
export const MONSTER_SCHEMA = {
  "$schema": "http://json-schema.org/draft-07/schema#",
  "title": "Monster Statblock (Master)",
  "type": "object",
  "required": ["schemaVersion", "type", "id", "data"],
  "properties": {
    "schemaVersion": { "type": "string", "description": "Semantic version for the schema (e.g., 1.0.0)." },
    "type": { "type": "string", "enum": ["monster"], "description": "Document kind." },
    "exportDate": { "type": "string", "format": "date-time" },
    "templateId": { "type": "string", "description": "Arbitrary template/source tag." },
    "id": { "type": "string", "description": "Unique stable identifier (slug/uuid)." },
    "source": {
      "type": "object",
      "properties": {
        "name": { "type": "string" },
        "page": { "type": "string" },
        "edition": { "type": "string", "enum": ["2014 PHB/MM", "2024 Core", "Homebrew", "Other"] },
        "license": { "type": "string" }
      },
      "additionalProperties": false
    },
    "tags": { "type": "array", "items": { "type": "string" }, "description": "Search/AI tags (e.g., 'undead', 'mythic', 'fey')." },
    "data": {
      "type": "object",
      "required": ["name", "size", "creatureType", "alignment", "cr", "ac", "hp", "speed", "abilities"],
      "properties": {
        "name": { "type": "string" },
        "pronunciation": { "type": "string" },
        "size": { "type": "string", "enum": ["Tiny","Small","Medium","Large","Huge","Gargantuan","Varies"] },
        "creatureType": {
          "type": "object",
          "properties": {
            "type": { "type": "string", "description": "e.g., Humanoid, Fiend, Undead" },
            "tags": { "type": "array", "items": { "type": "string" } }
          },
          "required": ["type"],
          "additionalProperties": false
        },
        "alignment": { "type": "string", "description": "Text as printed (e.g., 'Neutral Evil', 'Unaligned')." },

        "cr": {
          "type": "object",
          "properties": {
            "display": { "type": "string", "description": "Printed CR string (e.g., '1/4')." },
            "value": { "type": "number", "description": "Decimal CR (e.g., 0.25)." },
            "xp": { "type": "integer" },
            "pb": { "type": "integer", "description": "Proficiency bonus if explicitly given; else derive." }
          },
          "required": ["display"],
          "additionalProperties": false
        },

        "ac": {
          "type": "object",
          "properties": {
            "value": { "type": "integer" },
            "notes": { "type": "string", "description": "Armor source (natural armor, chain shirt, shield) or AC modes." },
            "modes": {
              "type": "array",
              "items": {
                "type": "object",
                "properties": {
                  "value": { "type": "integer" },
                  "condition": { "type": "string" }
                },
                "required": ["value"]
              }
            }
          },
          "required": ["value"]
        },

        "hp": {
          "type": "object",
          "properties": {
            "average": { "type": "integer" },
            "formula": { "type": "string", "description": "e.g., '2d6+0'." }
          },
          "required": ["average"]
        },

        "speed": {
          "type": "object",
          "properties": {
            "walk": { "type": "integer" },
            "burrow": { "type": "integer" },
            "climb": { "type": "integer" },
            "fly": { "type": "integer" },
            "hover": { "type": "boolean" },
            "swim": { "type": "integer" },
            "notes": { "type": "string" }
          }
        },

        "initiative": { "type": "integer", "description": "If you precompute it; else derive from DEX." },

        "abilities": {
          "type": "object",
          "properties": {
            "str": { "type": "integer" },
            "dex": { "type": "integer" },
            "con": { "type": "integer" },
            "int": { "type": "integer" },
            "wis": { "type": "integer" },
            "cha": { "type": "integer" }
          },
          "required": ["str","dex","con","int","wis","cha"]
        },

        "saves": {
          "type": "object",
          "description": "Only list trained saves or explicit modifiers.",
          "properties": {
            "str": { "type": "integer" },
            "dex": { "type": "integer" },
            "con": { "type": "integer" },
            "int": { "type": "integer" },
            "wis": { "type": "integer" },
            "cha": { "type": "integer" }
          },
          "additionalProperties": false
        },

        "skills": {
          "type": "object",
          "additionalProperties": {
            "type": "object",
            "properties": {
              "bonus": { "type": "integer" },
              "expertise": { "type": "boolean" }
            },
            "required": ["bonus"],
            "additionalProperties": false
          },
          "description": "Map of skill name → {bonus, expertise?}."
        },

        "damageModifiers": {
          "type": "object",
          "properties": {
            "resistances": { "$ref": "#/$defs/dmgList" },
            "immunities": { "$ref": "#/$defs/dmgList" },
            "vulnerabilities": { "$ref": "#/$defs/dmgList" }
          }
        },

        "conditionImmunities": { "type": "array", "items": { "type": "string" } },

        "senses": {
          "type": "object",
          "properties": {
            "blindsight": { "type": "integer" },
            "darkvision": { "type": "integer" },
            "tremorsense": { "type": "integer" },
            "truesight": { "type": "integer" },
            "passivePerception": { "type": "integer" },
            "notes": { "type": "string" }
          }
        },

        "languages": {
          "type": "object",
          "properties": {
            "list": { "type": "array", "items": { "type": "string" } },
            "telepathy": { "type": "integer", "description": "Range in ft if any." },
            "understandsBut": { "type": "string", "description": "E.g., 'understands X but can't speak'." }
          }
        },

        "environments": { "type": "array", "items": { "type": "string" }, "description": "Biomes/habitats." },

        "traits": {
          "type": "array",
          "items": { "$ref": "#/$defs/feature" },
          "description": "Static features like Magic Resistance, Pack Tactics, Keen Hearing."
        },

        "actions": { "type": "array", "items": { "$ref": "#/$defs/feature" } },
        "bonusActions": { "type": "array", "items": { "$ref": "#/$defs/feature" } },
        "reactions": { "type": "array", "items": { "$ref": "#/$defs/feature" } },

        "legendary": {
          "type": "object",
          "properties": {
            "usesPerRound": { "type": "integer", "description": "Usually 3." },
            "regainRule": { "type": "string", "description": "e.g., 'Regains spent uses at start of its turn'." },
            "actions": { "type": "array", "items": { "$ref": "#/$defs/feature" } },
            "resistances": {
              "type": "object",
              "properties": {
                "max": { "type": "integer" },
                "remaining": { "type": "integer" },
                "text": { "type": "string", "description": "Printed wording." }
              }
            }
          }
        },

        "lair": {
          "type": "object",
          "properties": {
            "initiative": { "type": "string", "description": "e.g., 'on initiative count 20 (losing ties)'" },
            "actions": { "type": "array", "items": { "$ref": "#/$defs/feature" } },
            "regionalEffects": { "type": "array", "items": { "type": "string" } },
            "dispelOnDeath": { "type": "boolean" }
          }
        },

        "mythic": {
          "type": "object",
          "properties": {
            "isMythic": { "type": "boolean" },
            "trigger": { "type": "string", "description": "When the mythic trait activates." },
            "phaseDescription": { "type": "string" },
            "actions": { "type": "array", "items": { "$ref": "#/$defs/feature" } }
          }
        },

        "spellcasting": {
          "type": "object",
          "properties": {
            "innate": {
              "type": "object",
              "properties": {
                "ability": { "type": "string", "enum": ["STR","DEX","CON","INT","WIS","CHA"] },
                "attackBonus": { "type": "integer" },
                "saveDC": { "type": "integer" },
                "spells": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "name": { "type": "string" },
                      "uses": { "type": "string", "description": "e.g., 'at will', '3/day each', '1/day'." },
                      "notes": { "type": "string" }
                    },
                    "required": ["name","uses"]
                  }
                }
              }
            },
            "prepared": {
              "type": "object",
              "properties": {
                "casterLevel": { "type": "integer" },
                "ability": { "type": "string", "enum": ["STR","DEX","CON","INT","WIS","CHA"] },
                "attackBonus": { "type": "integer" },
                "saveDC": { "type": "integer" },
                "slots": {
                  "type": "object",
                  "description": "Spell slots by level 1–9.",
                  "additionalProperties": { "type": "integer" }
                },
                "spellsByLevel": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "level": { "type": "integer" },
                      "spells": { "type": "array", "items": { "type": "string" } }
                    },
                    "required": ["level","spells"]
                  }
                }
              }
            }
          }
        },

        "equipment": { "type": "array", "items": { "type": "string" } },

        "images": {
          "type": "object",
          "properties": {
            "artwork": { "type": "string" },
            "token": { "type": "string" }
          }
        },

        "variants": {
          "type": "array",
          "items": {
            "type": "object",
            "properties": {
              "name": { "type": "string" },
              "diff": { "type": "object", "description": "Patch-like overrides to apply to base monster." }
            },
            "required": ["name","diff"]
          }
        },

        "forms": {
          "type": "array",
          "description": "Alternate forms/statblocks for shapechangers; each may fully or partially override base.",
          "items": {
            "type": "object",
            "properties": {
              "name": { "type": "string" },
              "diff": { "type": "object" }
            },
            "required": ["name","diff"]
          }
        },

        "notes": { "type": "string" },

        "systemExtensions": {
          "type": "object",
          "description": "Room for non‑RAW systems (e.g., Ascendant Combat).",
          "properties": {
            "ascendantCombat": {
              "type": "object",
              "properties": {
                "targetClass": { "type": "integer" },
                "armorCoverage": { "type": "string" },
                "penetration": {
                  "type": "object",
                  "properties": {
                    "piercing": { "type": "integer" },
                    "slashing": { "type": "integer" },
                    "bludgeoning": { "type": "integer" }
                  }
                }
              }
            }
          },
          "additionalProperties": true
        }
      },
      "additionalProperties": false
    }
  },

  "$defs": {
    "dmgQualifier": {
      "type": "object",
      "properties": {
        "type": { "type": "string", "description": "Damage type string: fire, cold, necrotic, nonmagical B/P/S, etc." },
        "qualifier": { "type": "string", "description": "e.g., 'from nonmagical attacks', 'that aren't silvered'." }
      },
      "required": ["type"],
      "additionalProperties": false
    },

    "dmgList": {
      "type": "array",
      "items": { "$ref": "#/$defs/dmgQualifier" }
    },

    "area": {
      "type": "object",
      "properties": {
        "shape": { "type": "string", "enum": ["line","cone","cube","sphere","cylinder","wall","custom"] },
        "size": { "type": "integer", "description": "Primary size (ft)." },
        "secondary": { "type": "integer", "description": "Thickness/length, etc., if needed." },
        "notes": { "type": "string" }
      }
    },

    "save": {
      "type": "object",
      "properties": {
        "ability": { "type": "string", "enum": ["STR","DEX","CON","INT","WIS","CHA"] },
        "dc": { "type": "integer" },
        "onSuccess": { "type": "string" },
        "onFailure": { "type": "string" },
        "halfOnSuccess": { "type": "boolean" },
        "conditionsInflicted": { "type": "array", "items": { "type": "string" } },
        "duration": { "type": "string" }
      }
    },

    "damageComponent": {
      "type": "object",
      "properties": {
        "dice": { "type": "string", "description": "e.g., '1d6+2' or '3d10'." },
        "avg": { "type": "number" },
        "type": { "type": "string" },
        "notes": { "type": "string" }
      },
      "required": ["dice","type"]
    },

    "attack": {
      "type": "object",
      "properties": {
        "type": { "type": "string", "enum": ["meleeWeapon","rangedWeapon","meleeSpell","rangedSpell","special"] },
        "toHit": { "type": "integer" },
        "reach": { "type": "integer" },
        "range": {
          "type": "object",
          "properties": {
            "normal": { "type": "integer" },
            "long": { "type": "integer" }
          }
        },
        "targets": { "type": "string", "description": "Text like 'one target'." },
        "damage": { "type": "array", "items": { "$ref": "#/$defs/damageComponent" } },
        "riders": { "type": "string", "description": "On-hit effects text." },
        "save": { "$ref": "#/$defs/save" },
        "area": { "$ref": "#/$defs/area" }
      }
    },

    "recharge": {
      "type": "object",
      "properties": {
        "type": { "type": "string", "enum": ["perShortRest","perLongRest","perTurn","perDay","rechargeRoll","limitedUses","atWill"] },
        "value": { "type": "string", "description": "E.g., '5-6', '3/day', '1/turn'." }
      }
    },

    "feature": {
      "type": "object",
      "properties": {
        "name": { "type": "string" },
        "activation": { "type": "string", "enum": ["action","bonusAction","reaction","legendary","lair","mythic","passive"] },
        "cost": { "type": "number", "description": "Legendary action cost, etc." },
        "recharge": { "$ref": "#/$defs/recharge" },
        "attack": { "$ref": "#/$defs/attack" },
        "save": { "$ref": "#/$defs/save" },
        "area": { "$ref": "#/$defs/area" },
        "kind": { "type": "string", "enum": ["multiattack","meleeWeaponAttack","rangedWeaponAttack","meleeSpellAttack","rangedSpellAttack","ability","areaEffect","utility","standardAction"] },
        "standardActionKey": { "type": "string", "description": "Exact standard-action resolver key; never inferred from the display name." },
        "activationType": { "type": "string", "enum": ["action","bonus action","reaction","legendary action"] },
        "actionType": { "type": "string", "enum": ["action","bonus_action","reaction","legendary_action"] },
        "type": { "type": "string" },
        "executionRoute": { "type": "string", "enum": ["attack","spell","ability","standard"] },
        "targetRequirement": { "type": "string", "enum": ["none","self","creature","object","point","area","trigger"] },
        "ruleReference": { "type": "string" },
        "featureKey": { "type": "string", "description": "Exact registered ability resolver key." },
        "text": { "type": "string", "description": "Rules text if not an attack/save." }
      },
      "required": ["name"]
    }
  }
};

// Utility functions for working with the monster schema
export class MonsterSchemaUtils {
  /**
   * Creates an empty monster template following the new schema
   */
  static createEmptyMonster() {
    return {
      schemaVersion: "1.0.0",
      type: "monster",
      id: `monster_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: {
        name: "Custom",
        edition: "2024 Core"
      },
      tags: [],
      data: {
        name: "New Monster",
        size: "Medium",
        creatureType: { type: "Humanoid" },
        alignment: "Unaligned",
        cr: { display: "1/4", value: 0.25, xp: 50 },
        ac: { value: 10 },
        hp: { average: 4, formula: "1d8" },
        speed: { walk: 30 },
        abilities: {
          str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10
        },
        senses: { passivePerception: 10 },
        languages: { list: ["Common"] },
        traits: [],
        actions: []
      }
    };
  }

  /**
   * Migrates old monster format to new schema
   */
  static migrateOldMonster(oldMonster) {
    const migrated = {
      schemaVersion: "1.0.0",
      type: "monster",
      id: oldMonster.id || oldMonster.name.toLowerCase().replace(/\s+/g, '_'),
      source: {
        name: "Legacy Data",
        edition: "2014 PHB/MM"
      },
      tags: oldMonster.tags || [],
      data: {
        name: oldMonster.name,
        size: oldMonster.size || "Medium",
        creatureType: { 
          type: oldMonster.type || "Humanoid",
          tags: oldMonster.creatureTypeTags || []
        },
        alignment: oldMonster.alignment || "Unaligned",
        cr: {
          display: oldMonster.cr || "1/4",
          value: this.parseCR(oldMonster.cr),
          xp: this.getXPForCR(oldMonster.cr)
        },
        ac: { 
          value: oldMonster.ac || 10,
          notes: oldMonster.acNotes
        },
        hp: { 
          average: oldMonster.hp || oldMonster.maxHp || 4,
          formula: oldMonster.hpFormula
        },
        speed: {
          walk: oldMonster.speed || 30,
          climb: oldMonster.climbSpeed,
          fly: oldMonster.flySpeed,
          swim: oldMonster.swimSpeed,
          burrow: oldMonster.burrowSpeed
        },
        initiative: oldMonster.initiative,
        abilities: oldMonster.stats || oldMonster.abilities || {
          str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10
        },
        saves: oldMonster.savingThrows || oldMonster.saves,
        skills: this.migrateSkills(oldMonster.skills),
        damageModifiers: {
          resistances: this.migrateDamageList(oldMonster.damageResistances),
          immunities: this.migrateDamageList(oldMonster.damageImmunities),
          vulnerabilities: this.migrateDamageList(oldMonster.damageVulnerabilities)
        },
        conditionImmunities: oldMonster.conditionImmunities || [],
        senses: this.migrateSenses(oldMonster.senses, oldMonster.darkvision, oldMonster.blindsight),
        languages: this.migrateLanguages(oldMonster.languages),
        environments: oldMonster.environments || [],
        traits: this.migrateFeatures(oldMonster.specialAbilities || oldMonster.traits, 'passive'),
        actions: this.migrateFeatures(oldMonster.actions, 'action'),
        bonusActions: this.migrateFeatures(oldMonster.bonusActions, 'bonusAction'),
        reactions: this.migrateFeatures(oldMonster.reactions, 'reaction'),
        equipment: oldMonster.equipment || [],
        images: {
          artwork: oldMonster.artwork || oldMonster.image,
          token: oldMonster.token
        },
        notes: oldMonster.notes || oldMonster.description
      }
    };

    return migrated;
  }

  // Helper methods for migration
  static parseCR(cr) {
    if (!cr) return 0.25;
    if (cr === "1/8") return 0.125;
    if (cr === "1/4") return 0.25;
    if (cr === "1/2") return 0.5;
    return parseFloat(cr) || 0.25;
  }

  static getXPForCR(cr) {
    const xpTable = {
      "0": 0, "1/8": 25, "1/4": 50, "1/2": 100,
      "1": 200, "2": 450, "3": 700, "4": 1100, "5": 1800
    };
    return xpTable[cr] || 50;
  }

  static migrateSkills(oldSkills) {
    if (!oldSkills) return {};
    const newSkills = {};
    for (const [skill, bonus] of Object.entries(oldSkills)) {
      newSkills[skill] = { bonus: bonus, expertise: false };
    }
    return newSkills;
  }

  static migrateDamageList(oldList) {
    if (!oldList) return [];
    return oldList.map(dmg => ({ type: dmg }));
  }

  static migrateSenses(oldSenses, darkvision, blindsight) {
    const senses = { passivePerception: 10 };
    if (darkvision) senses.darkvision = darkvision;
    if (blindsight) senses.blindsight = blindsight;
    if (oldSenses) senses.notes = oldSenses;
    return senses;
  }

  static migrateLanguages(oldLanguages) {
    if (!oldLanguages) return { list: [] };
    if (Array.isArray(oldLanguages)) {
      return { list: oldLanguages };
    }
    if (typeof oldLanguages === 'string') {
      return { list: oldLanguages.split(',').map(l => l.trim()) };
    }
    return { list: [] };
  }

  static migrateFeatures(oldFeatures, defaultActivation = 'action') {
    if (!oldFeatures) return [];
    return oldFeatures.map(feature => {
      const migrated = {
        name: feature.name,
        activation: feature.activation || defaultActivation,
        cost: feature.cost,
        recharge: feature.recharge,
        attack: feature.attack,
        save: feature.save,
        area: feature.area,
        kind: feature.kind,
        standardActionKey: feature.standardActionKey,
        activationType: feature.activationType,
        actionType: feature.actionType,
        type: feature.type,
        executionRoute: feature.executionRoute,
        targetRequirement: feature.targetRequirement,
        ruleReference: feature.ruleReference,
        text: feature.description || feature.text
      };
      Object.keys(migrated).forEach((key) => migrated[key] === undefined && delete migrated[key]);
      return migrated;
    });
  }
}

export default MonsterSchemaUtils;
